import { AssetVisitYardEventType } from './asset-visit';
import { DatabaseEntities, INovaEntity } from './base';
import { Maybe } from './globals';

export enum YardViewModeEnum {
  GRID = 'grid',
  LIST = 'list',
  TV = 'tv'
}

export enum YardTasksViewModeEnum {
  BOARD = 'board',
  LIST = 'list'
}

export enum SpotType {
  PARKING = 'parking',
  DOCKING = 'docking'
}

export enum SpotStatus {
  OPEN = 'open',
  CLOSED = 'closed',
  ASSIGNED = 'assigned',
  RESERVED_MANUAL = 'reserved_manual',
  RESERVED_AUTOMATED = 'reserved_automated',
  UNKNOWN = 'unknown'
}

/**
 * The two statuses that represent a reserved spot. Use this as the single source
 * of truth (e.g. `SPOT_STATUSES_RESERVED.includes(status)`) instead of repeating
 * `status === RESERVED_MANUAL || status === RESERVED_AUTOMATED`.
 */
export const SPOT_STATUSES_RESERVED: readonly SpotStatus[] = Object.freeze([
  SpotStatus.RESERVED_MANUAL,
  SpotStatus.RESERVED_AUTOMATED
]) as readonly SpotStatus[];

export enum SpotEventTypeEnum {
  ASSIGNMENT = 'assignment',
  RELEASE = 'release',
  RESERVE = 'reserve',
  RELEASE_RESERVE = 'release_reserve',
  CLOSE = 'close',
  OPEN = 'open',
  OTHER = 'other'
}

export enum SpotReserveEntityEnum {
  YardTask = DatabaseEntities.YardTask
}

export interface ISpot {
  id: string;
  code: string;
  type: SpotType;
  isActive: boolean;
  status?: SpotStatus;
  spotAssignments?: ISpotAssignment[];
  spotReserves?: ISpotReserve[];
  /** Yard-view BFF: open move tasks when present (spot grid / detail / list) */
  openMoveTasks?: Maybe<Array<{ id: string; refNum?: Maybe<string> }>>;
}

export interface ISpotAssignment extends INovaEntity {
  id: string;
  spotId: string;
  warehouseId: string;
  assetVisitId?: Maybe<string>;
  assetContainerId?: Maybe<string>;
}

export interface ISpotReserve extends INovaEntity {
  id: string;
  spotId: string;
  warehouseId: string;
  objectId: string;
  entityName: SpotReserveEntityEnum;
}

export enum DepartTypeEnum {
  All = 'All',
  AssetVisit = DatabaseEntities.AssetVisit,
  AssetContainer = DatabaseEntities.AssetContainer
}

/**
 * Spot open/closed/assigned/reserved/locked state is derived entirely from
 * `Spot.status` (NOT NULL in the DB). `isActive` is a soft-delete flag only and
 * is never consulted here. Returns UNKNOWN only when the status is truly absent.
 */
export function getSpotStatus(spot: ISpot): SpotStatus {
  return spot?.status ?? SpotStatus.UNKNOWN;
}

/*
 * The isSpotAssigned*…To… helpers below answer *what* a spot is assigned to
 * (asset visit vs. asset container), not merely whether it is assigned.
 * `Spot.status` only encodes `ASSIGNED` — it does not carry the target type —
 * so these helpers must keep reading the `spotAssignments` relation. Use
 * `getSpotStatus(spot) === SpotStatus.ASSIGNED` to answer "is it assigned at
 * all"; use these helpers only when the target type matters.
 */
export function isSpotAssignedToAssetVisit(spot: ISpot): boolean {
  return spot.spotAssignments?.some(assignment => Boolean(assignment.assetVisitId)) ?? false;
}

export function isSpotAssignedOnlyToAssetVisit(spot: ISpot): boolean {
  return spot.spotAssignments?.every(assignment => Boolean(assignment.assetVisitId)) ?? false;
}

export function isSpotAssignedToAssetContainer(spot: ISpot): boolean {
  return spot.spotAssignments?.some(assignment => Boolean(assignment.assetContainerId)) ?? false;
}

export function isSpotAssignedOnlyToAssetContainer(spot: ISpot): boolean {
  return spot.spotAssignments?.every(assignment => Boolean(assignment.assetContainerId)) ?? false;
}

/*
 * Sorts the spot list by spot code using Natural Sorting algorithm, IN-PLACE
 */
export function sortSpots(spots: ISpot[]): void {
  const extractNumber = (code: string) => {
    const match = code.match(/\d+/u);
    return match ? parseInt(match[0], 10) : Infinity;
  };

  const extractAlpha = (code: string) => {
    const match = code.match(/[a-zA-Z]+/u);
    return match ? match[0] : '';
  };

  if (
    Array.isArray(spots) &&
    spots.every(spot => typeof spot === 'object' && spot !== null && 'code' in spot)
  ) {
    spots.sort((a, b) => {
      const alphaA = extractAlpha(a.code);
      const alphaB = extractAlpha(b.code);
      if (alphaA === alphaB) {
        return extractNumber(a.code) - extractNumber(b.code);
      }
      return alphaA.localeCompare(alphaB);
    });
  }
}

export const YARD_SPOT_CODE_MAX_LENGTH = 5;

export function getSpotEventType(
  eventType: SpotEventTypeEnum,
  spotType: SpotType
): AssetVisitYardEventType | undefined {
  if (eventType === SpotEventTypeEnum.ASSIGNMENT) {
    if (spotType === SpotType.DOCKING) {
      return AssetVisitYardEventType.Docked;
    }
    return AssetVisitYardEventType.Parked;
  } else if (eventType === SpotEventTypeEnum.RELEASE) {
    return AssetVisitYardEventType.Unassigned;
  }
}
